import React from 'react';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

interface PDFColumn {
  title: string;
  width?: string | number;
  dataKey?: string;
  align?: 'left' | 'center' | 'right';
  wrap?: boolean;
}

interface ComponentProps {
  columns: PDFColumn[];
  data: Record<string, any>[];
  headerStyle?: any;
  rowStyle?: any;
  cellStyle?: any;
  alternateRows?: boolean;
}

const styles = StyleSheet.create({
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #ccc',
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #ddd',
    minHeight: 25,
    alignItems: 'stretch',
  },
  tableRowAlternate: {
    backgroundColor: '#f9f9f9',
  },
  tableCell: {
    paddingHorizontal: 2,
    paddingVertical: 4,
    fontSize: 8,
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'hidden',
    borderRight: '1px solid #ddd',
  },
  headerCell: {
    paddingHorizontal: 2,
    paddingVertical: 6,
    fontSize: 9,
    fontWeight: 'bold',
    display: 'flex',
    borderRight: '1px solid #ccc',
  },
  textLeft: {
    alignItems: 'flex-start',
  },
  textCenter: {
    alignItems: 'center',
  },
  textRight: {
    alignItems: 'flex-end',
  },
});

const Table: React.FC<ComponentProps> = ({
  columns,
  data,
  headerStyle = {},
  rowStyle = {},
  cellStyle = {},
  alternateRows = true,
}) => {
  const getColumnWidth = (column: PDFColumn) => {
    if (column.width) {
      return typeof column.width === 'string' ? column.width : `${column.width}px`;
    }
    return `${100 / columns.length}%`;
  };

  const getAlignmentStyle = (align?: string) => {
    switch (align) {
      case 'center':
        return styles.textCenter;
      case 'right':
        return styles.textRight;
      default:
        return styles.textLeft;
    }
  };

  const getCellValue = (row: Record<string, any>, column: PDFColumn, index: number) => {
    if (column.dataKey) {
      return row[column.dataKey] ?? '';
    }
    // Fallback to array index if no dataKey provided
    return Array.isArray(row) ? (row[index] ?? '') : '';
  };

  return (
    <View style={[styles.table, headerStyle]}>
      {/* Table Header */}
      <View style={[styles.tableHeader, headerStyle]}>
        {columns.map((column, index) => (
          <View
            key={`header-${index}`}
            style={[styles.headerCell, { width: getColumnWidth(column) }, getAlignmentStyle(column.align)]}
          >
            <Text>{column.title}</Text>
          </View>
        ))}
      </View>

      {/* Table Rows */}
      {data.map((row, rowIndex) => (
        <View
          key={`row-${rowIndex}`}
          style={[styles.tableRow, alternateRows && rowIndex % 2 === 1 && styles.tableRowAlternate, rowStyle]}
        >
          {columns.map((column, colIndex) => (
            <View
              key={`cell-${rowIndex}-${colIndex}`}
              style={[styles.tableCell, { width: getColumnWidth(column) }, getAlignmentStyle(column.align), cellStyle]}
            >
              <Text>{getCellValue(row, column, colIndex)}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

export default Table;
